// API クライアント（05 §1.1 契約ファースト）。
//
// **型の出所は OpenAPI**（`pnpm gen:api` が backend の /openapi.json から `schema.d.ts` を生成）。
// 手で型を写経しない＝API と画面がズレたら typecheck が落ちる、という状態を保つ。
//
// ここが持つのは「HTTP の作法」だけ：credentials・CSRF ヘッダ・統一エラーエンベロープの展開。

import { config } from "@kenzai/config";

/** 統一エラーエンベロープ（05 §1.3）。バックエンドの errors.py と対。 */
export type ApiErrorCode =
  | "validation_error"
  | "unauthorized"
  | "forbidden"
  | "not_found"
  | "conflict_stale_update"
  | "idempotency_conflict"
  | "business_rule"
  | "rate_limited"
  | "internal";

export type FieldError = { field: string; code: string; message: string };

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly status: number,
    readonly requestId?: string,
    readonly fieldErrors: FieldError[] = [],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Options = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** 職人（liff）の Bearer トークン。指定時は Cookie を送らない（05 §3）。 */
  bearer?: string;
  /** 冪等キー（05 §1.4）＝作成系で必須。 */
  idempotencyKey?: string;
};

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {
    // カスタムヘッダ＝CSRF 対策の一部（02 §4.2）。GET にも付けて経路を1つに保つ。
    [config.csrfHeader]: "1",
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.bearer) headers["Authorization"] = `Bearer ${opts.bearer}`;
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

  const res = await fetch(`${config.apiBase}${path}`, {
    method,
    headers,
    // 卸・運営＝セッションCookie（httpOnly）を送る。JS からトークンを触らない＝XSS で盗めない。
    // 職人＝Bearer なので Cookie は不要（05 §3：Cookie不使用＝CSRF 対象外）。
    credentials: opts.bearer ? "omit" : "include",
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    const err = (json as { error?: Record<string, unknown> } | undefined)?.error;
    throw new ApiError(
      (err?.code as ApiErrorCode) ?? "internal",
      (err?.message as string) ?? "エラーが発生しました。",
      res.status,
      err?.request_id as string | undefined,
      (err?.field_errors as FieldError[]) ?? [],
    );
  }
  return json as T;
}

/**
 * 印刷用 HTML を **POST** で取り、新規タブで開く（D-76・M1.5・05 §4.5）。
 *
 * ★**`window.open(url)` や `<a href … target="_blank">` を置かない**＝どちらも GET になり、
 *   発行（＝監査ログへの INSERT を伴う副作用のある操作）が GET に載る。本システムは
 *   「GET に副作用なし」を CSRF 防御の前提にしているので、置いた瞬間に
 *   「外部サイトのリンクを1回踏ませるだけで卸の名前で監査行を作れる」穴が開く。
 *
 * ★**ユーザー操作と同じイベント連鎖の中で呼ぶ**＝そうしないとポップアップブロックに当たる。
 *   だから `window.open()` を **await の前**に済ませ、取れた HTML を後から流し込む。
 *
 * ★Blob URL は**開いた後に revoke**（1回の印刷ごとにメモリへ残さない）。
 */
export async function printHtml(path: string, body?: unknown): Promise<void> {
  // 先に開く＝クリックのイベント連鎖の中なのでブロックされない。
  const tab = window.open("", "_blank");
  const headers: Record<string, string> = { [config.csrfHeader]: "1" };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(`${config.apiBase}${path}`, {
      method: "POST",
      headers,
      credentials: "include",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    tab?.close();
    throw e;
  }

  if (!res.ok) {
    // エラーは JSON（統一エンベロープ）で返る＝開いたタブは閉じて、呼び出し側に投げる。
    tab?.close();
    const text = await res.text();
    const err = (text ? (JSON.parse(text) as { error?: Record<string, unknown> }) : undefined)?.error;
    throw new ApiError(
      (err?.code as ApiErrorCode) ?? "internal",
      (err?.message as string) ?? "帳票を発行できませんでした。",
      res.status,
      err?.request_id as string | undefined,
      (err?.field_errors as FieldError[]) ?? [],
    );
  }

  const url = URL.createObjectURL(new Blob([await res.text()], { type: "text/html" }));
  if (tab) {
    tab.location.href = url;
  } else {
    // ポップアップが塞がれていた場合の逃げ道（同じタブで開く＝印刷後に戻れる）。
    window.location.href = url;
  }
  // revoke は少し待つ＝即座に消すと、遅い環境でタブが読み込む前に無効になる。
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ---------------------------------------------------------------------------
// identity（05 §4.1）。M1 で生成型（schema.d.ts）に差し替える。
// ---------------------------------------------------------------------------

export type TenantSummary = { id: number; code: string; name: string };
export type ConsoleMe = {
  id: number;
  email: string;
  name: string;
  role: "admin" | "staff";
  tenant: TenantSummary;
};
export type OpsMe = { id: number; email: string; name: string; role: "ops_admin" | "ops_member" };
export type PartnerToken = {
  access_token: string;
  token_type: string;
  expires_in: number;
  builder_user_id: number;
  status: "unlinked" | "active" | "blocked";
};

export const auth = {
  login: (email: string, password: string) =>
    api<ConsoleMe>("/api/v1/auth/login", { method: "POST", body: { email, password } }),
  logout: () => api<{ ok: boolean }>("/api/v1/auth/logout", { method: "POST" }),
  me: () => api<ConsoleMe>("/api/v1/console/me"),
  /** FR-112①：自己申告のパスワード再設定（D-81⑤）。
   *  ★**応答は常に同一**（存在しないアドレスでも 200）＝ここで結果を出し分けないこと。
   *  出し分けると、サーバ側で文言を揃えてアカウント列挙を防いだ意味が消える。 */
  forgotPassword: (email: string) =>
    api<{ ok: boolean }>("/api/v1/auth/password/forgot", { method: "POST", body: { email } }),
  setupPassword: (token: string, newPassword: string) =>
    api<{ ok: boolean }>("/api/v1/auth/password/setup", {
      method: "POST",
      body: { token, new_password: newPassword },
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ ok: boolean }>("/api/v1/auth/password/change", {
      method: "POST",
      body: { current_password: currentPassword, new_password: newPassword },
    }),
  opsMe: () => api<OpsMe>("/api/v1/ops/me"),
  opsLogout: () => api<{ ok: boolean }>("/api/v1/auth/ops/logout", { method: "POST" }),
  liff: (idToken: string) =>
    api<PartnerToken>("/api/v1/auth/liff", { method: "POST", body: { id_token: idToken } }),
};

// M1 の API（05 §4.2/§4.3/§4.5/§4.6）
export * from "./kenzai";
